import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectorRef, Component, DestroyRef, inject, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormBuilder, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    ContactDetailsResponse,
    CreationMethod,
    SubjectApiService,
    SubjectCategory,
    SubjectCategoryCreationReason,
    SubjectCheckInfo,
    SubjectRequest,
    SubjectResponse,
    SubjectType,
} from '@npck/af-frontend-api';
import {BIN_PATTERN, DATE_SERVER_FORMAT, IIN_PATTERN, MAX_LENGTH, RouterService, rxSpinner, Spinner, TAB} from '@npck/libs/commons';
import {DialogService, SnackbarService, StepperComponent} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {cloneDeep, isEqual} from 'lodash-es';
import {catchError, debounceTime, EMPTY, forkJoin, Observable, of, Subject, takeUntil, throwError} from 'rxjs';
import {DateForm} from '../../../components/date-picker-field/date-picker-field.component';
import {PhoneNumberComponent} from '../../../components/phone-number/phone-number.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {
    CARD_NUMBER_MAX_LENGTH,
    FOREIGN_IBAN_MIN_LENGTH,
    FOREIGN_IBAN_PATTERN,
    IBAN_MAX_LENGTH,
    KZ_IBAN_LENGTH,
    KZ_IBAN_PATTERN,
    MAX_PAYMENT_POINT_NUMBER,
    NUMBER_PATTERN,
    PATTERN_PAYMENT_POINT_NUMBER,
    PAYMENT_POINT_MAX_LENGTH,
    WALLET_NUMBER_MAX_LENGTH,
    WALLET_NUMBER_MIN_LENGTH,
} from '../../../domain/constants';
import {getValidPhoneNumberOrNull, PhoneInfo} from '../../../domain/phone-utils';
import {SUBJECT_ID} from '../../../domain/query-params.keys';
import {DATE_FORMATS} from '../../../infra/date-time.adapter';
import {Path} from '../../../path';
import {showServerValidationErrors, ValidationErrorItem} from '../../../utils/server.field.validation.utils';
import {AF_CENTRE_TABS, AfCentreTab} from '../../af-centre/af-centre.component';
import {
    ContactDetailsForm,
    createContactDetailsRequest,
} from '../../incidents/create-edit/contact-details-step/contact-details-step.component';
import {SubjectsTab} from '../subjects.component';

type SubjectStep = 'basicInfo' | 'subjectContactDetails';

@Component({
    templateUrl: 'create-edit-subject.component.html',
    styleUrls: ['create-edit-subject.component.scss'],
    standalone: false,
})
export class CreateEditSubjectComponent {
    @ViewChild('phoneNumberComponent') phoneNumberComponent: PhoneNumberComponent;
    @ViewChild('stepper') stepper: StepperComponent;
    Spinner = Spinner;
    IBAN_MAX_LENGTH = IBAN_MAX_LENGTH;
    KZ_IBAN_LENGTH = KZ_IBAN_LENGTH;
    DATE_FORMATS = DATE_FORMATS;
    Validators = Validators;

    form: FormGroup<CreateSubjectForm>;
    contactDetailsForm: FormGroup<ContactDetailsForm>;
    isPotentialVictim: boolean = false;
    existingSubjectMode: boolean = false;
    steps: SubjectStep[] = ['basicInfo', 'subjectContactDetails'];
    currentStep: SubjectStep = 'basicInfo';
    subjectId: string;
    subjectResponse: SubjectResponse;
    existingContactDetails: ContactDetailsResponse[] = [];
    isEditMode = false;
    isContactDetailsChanged = false;
    canEditPersonalData = false;
    isFormChanged = false;
    excludedCategories: string[] = [];
    protected readonly SubjectType = SubjectType;
    private tab: SubjectsTab;
    private selectedCountryCode: PhoneInfo;
    private destroyRef = inject(DestroyRef);
    private formSubscriptions$ = new Subject<void>();
    private initialFormValue: FormGroup<CreateSubjectForm>['value'];
    private initialContactDetailsValue: FormGroup<ContactDetailsForm>['value'];

    constructor(
        private formBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        private subjectApiService: SubjectApiService,
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private snackbarService: SnackbarService,
        private dialogService: DialogService,
        private authHolderService: AuthHolderService,
    ) {
        this.tab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as SubjectsTab;
        this.subjectId = this.activatedRoute.snapshot.paramMap.get(SUBJECT_ID);
        this.form = this.formBuilder.group({subjectType: new FormControl(null, [Validators.required])});
        this.contactDetailsForm = this.formBuilder.group({});
        this.excludedCategories = this.canSelectPotentialVictim ? ['POTENTIAL_SUSPECT'] : ['POTENTIAL_VICTIM', 'POTENTIAL_SUSPECT'];

        if (this.subjectId) {
            this.isEditMode = true;
            forkJoin({
                subject: this.subjectApiService.getSubject(this.subjectId),
                contactDetails: this.subjectApiService.getSubjectContactDetails(this.subjectId),
            })
                .pipe(rxSpinner('initial'))
                .subscribe(({subject, contactDetails}) => {
                    this.subjectResponse = subject;
                    const orgBin = this.authHolderService.organizationBin;
                    const activeContacts = contactDetails.filter(c => !c.isDeleted);
                    this.existingContactDetails = this.isNpckAdmin
                        ? activeContacts
                        : activeContacts.filter(c => !orgBin || c.createdByOrganization === orgBin);
                    this.buildEditForm(subject);
                });
        } else {
            this.form.controls.subjectType.valueChanges
                .subscribe(() => {
                    if (this.existingSubjectMode) {
                        return;
                    }
                    this.updateForm();
                });
        }
    }

    get potentialVictimCategory(): {creationReason?: string; creationDescription?: string} | null {
        if (!this.subjectResponse) {
            return null;
        }
        return {
            creationReason: this.subjectResponse.creationReason,
            creationDescription: this.subjectResponse.creationDescription,
        };
    }

    get isPotentialVictimOnlyUser(): boolean {
        return this.authHolderService.hasAccess(['potential-victim-manager'])
            && !this.authHolderService.hasAccess(['npck-admin']);
    }

    get isNpckAdmin(): boolean {
        return this.authHolderService.hasAccess(['npck-admin']);
    }

    get canSelectPotentialVictim(): boolean {
        return this.authHolderService.hasAccess(['npck-admin', 'potential-victim-manager']);
    }

    get hasPotentialVictimCategory(): boolean {
        return !!this.subjectResponse?.categories
            ?.some(c => c.category === SubjectCategory.POTENTIAL_VICTIM && !c.isDeleted);
    }

    get activeCategoriesCount(): number {
        return this.subjectResponse?.categories?.filter(c => !c.isDeleted)?.length ?? 0;
    }

    get isMultiCategory(): boolean {
        return this.activeCategoriesCount > 1;
    }

    get isAuthor(): boolean {
        const orgBin = this.authHolderService.organizationBin;
        if (!orgBin) {
            return false;
        }
        const pvCategory = this.subjectResponse?.categories
            ?.find(c => c.category === SubjectCategory.POTENTIAL_VICTIM && !c.isDeleted);
        return pvCategory?.createdByOrganization === orgBin;
    }

    get editMode(): SubjectEditMode {
        if (!this.hasPotentialVictimCategory) {
            return 'NO_PV';
        }
        if (!this.isAuthor) {
            return 'CONTACT_ONLY';
        }
        if (this.isMultiCategory) {
            return 'LIMITED';
        }
        return 'FULL';
    }

    get isLastStep(): boolean {
        return this.isPotentialVictim && this.currentStep === 'subjectContactDetails';
    }

    get canSaveWithoutContacts(): boolean {
        const contacts = this.contactDetailsForm?.controls?.contacts?.controls ?? [];
        return contacts.every(contact => contact.pristine);
    }

    nextStep(): void {
        if (this.form.invalid) {
            return;
        }
        this.selectedCountryCode = this.phoneNumberComponent?.selectedCountryCode;
        if (this.isEditMode || this.existingSubjectMode) {
            this.stepper.nextStep();
            if (this.isEditMode && !this.initialContactDetailsValue) {
                setTimeout(() => this.captureInitialContactDetails());
            }
            return;
        }
        const value = this.form.getRawValue();
        this.subjectApiService.checkSubject({
            subjectType: value.subjectType,
            identificationNumber: value.iin ?? value.bin,
        })
            .pipe(rxSpinner('nextStep'))
            .subscribe(checkResponse => {
                if (!checkResponse.subject) {
                    this.stepper.nextStep();
                    return;
                }
                if (checkResponse.isPotentialVictim) {
                    this.showAlreadyPotentialVictimDialog(checkResponse.subject.id);
                } else {
                    this.showSubjectInOtherListsDialog(checkResponse.subject);
                }
            });
    }

    navigateBack = (): Observable<boolean> => {
        if (this.currentStep === 'subjectContactDetails') {
            this.stepper?.previousStep();
            return of(false);
        }
        if (this.isEditMode) {
            this.routerService.navigateToTab(`${Path.SUBJECTS}/${this.subjectId}`, this.tab);
            return of(false);
        }
        return of(true);
    };

    cancelToPotentialVictimsTab(): void {
        this.routerService.navigateToTab(Path.AF_CENTRE, 'tabPotentialVictim');
    }

    submit(): void {
        if (this.form.invalid) {
            return;
        }
        if (this.isEditMode && !this.isFormChanged && !this.isContactDetailsChanged) {
            this.routerService.navigateToTab(`${Path.SUBJECTS}/${this.subjectId}`, this.tab);
            return;
        }
        const value = this.form.getRawValue();
        const contactDetailsFormArray = this.contactDetailsForm?.controls?.contacts?.controls ?? [];
        const relevantContactForms = this.isEditMode
            ? contactDetailsFormArray
            : contactDetailsFormArray.filter(contact => contact.dirty);
        const contactDetails = relevantContactForms
            .map(contact => createContactDetailsRequest(contact))
            .filter(contact => this.editMode !== 'CONTACT_ONLY' || this.isNpckAdmin || !contact.id);
        const request: SubjectRequest = {
            creationMethod: CreationMethod.MANUAL,
            subjectType: value.subjectType,
            category: value.subjectCategory,
            country: value.country,
            identificationNumber: value.iin ?? value.bin,
            origin_date: dayjs(value.originDate.date).format(DATE_SERVER_FORMAT),
            surname: value.surname,
            name: value.name,
            patronymic: value.patronymic,
            gender: value.gender,
            type: value.type,
            phone_numbers: [getValidPhoneNumberOrNull(value.phone, this.phoneNumberComponent?.selectedCountryCode ??
                this.selectedCountryCode)],
            creationReason: value.creationReason,
            description: value.description,
            contactDetails,
            ...this.buildRequisiteData(value),
        };

        const afCentreTab = this.resolveAfCentreTab(value.subjectCategory);
        if (this.isEditMode) {
            this.subjectApiService.updateSubject(this.subjectId, request)
                .pipe(rxSpinner('submit'), catchError(this.handleValidationErrors))
                .subscribe(() => {
                    this.snackbarService.showSnackBar('i18n_af_snackBarMessageSubjectUpdated');
                    const returnTab = (afCentreTab as SubjectsTab) ?? this.tab;
                    this.routerService.navigateToTab(`${Path.SUBJECTS}/${this.subjectId}`, returnTab);
                });
        } else {
            this.subjectApiService.createSubject(request)
                .pipe(rxSpinner('submit'), catchError(this.handleValidationErrors))
                .subscribe(response => {
                    this.snackbarService.showSnackBar('i18n_af_snackBarMessageSubjectCreated');
                    if (afCentreTab && response?.id) {
                        this.routerService.navigateToTab(
                            `${Path.SUBJECTS}/${response.id}`,
                            afCentreTab as SubjectsTab,
                        );
                        return;
                    }
                    const path = AF_CENTRE_TABS.includes(this.tab) ? Path.AF_CENTRE : Path.SUBJECTS;
                    this.routerService.navigateToTab(path, this.tab);
                });
        }
    }

    onIbanInput(event: Event, controlName: 'ibanKz' | 'ibanForeign'): void {
        const input = event.target as HTMLInputElement;
        const cleaned = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
            .slice(0, controlName === 'ibanKz' ? KZ_IBAN_LENGTH : IBAN_MAX_LENGTH);
        this.form.controls[controlName]?.setValue(cleaned);
        this.form.controls[controlName]?.markAsTouched();
    }

    onWalletNumberInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.form.controls.walletNumber.setValue(input.value.toUpperCase());
    }

    paymentPointNumberInput(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const cleaned = inputElement.value
            .toUpperCase()
            .replace(/[^A-Z0-9 ]/g, '')
            .replace(/\s{2,}/g, ' ')
            .slice(0, PAYMENT_POINT_MAX_LENGTH);
        this.form.controls.paymentPointNumber.setValue(cleaned, {emitEvent: false});
        this.form.controls.paymentPointNumber.markAsTouched();
    }

    private handleValidationErrors = (error: HttpErrorResponse): Observable<never> => {
        const validationErrors: ValidationErrorItem[] = error.error?.errors;
        if (error.status !== 400 || !validationErrors?.length) {
            return throwError(() => error);
        }
        const identificationNumberControl = this.form.controls.iin ?? this.form.controls.bin;
        const fieldErrors = validationErrors.filter(item =>
            item.code === 'SUBJECT_IDENTIFICATION_NUMBER_INVALID' && item.field === 'identificationNumber');
        if (!identificationNumberControl || fieldErrors.length === 0) {
            return throwError(() => error);
        }
        if (this.currentStep === 'basicInfo') {
            showServerValidationErrors(fieldErrors, () => identificationNumberControl);
        } else {
            this.stepper?.previousStep();
            setTimeout(() => showServerValidationErrors(fieldErrors, () => identificationNumberControl));
        }
        const otherErrors = validationErrors.filter(item => !fieldErrors.includes(item));
        if (otherErrors.length === 0) {
            return EMPTY;
        }
        error.error.errors = otherErrors;
        return throwError(() => error);
    };

    private resolveAfCentreTab(category: SubjectCategory): AfCentreTab | null {
        switch (category) {
            case SubjectCategory.VICTIM:
                return 'tabVictim';
            case SubjectCategory.DROPPER:
                return 'tabDropper';
            case SubjectCategory.POTENTIAL_VICTIM:
                return 'tabPotentialVictim';
            default:
                return null;
        }
    }

    private buildEditForm(subject: SubjectResponse): void {
        const activeCategories = subject.categories?.filter(c => !c.isDeleted) ?? [];
        const mode = this.editMode;
        this.canEditPersonalData = mode === 'FULL' || mode === 'NO_PV';
        this.isPotentialVictim = true;
        this.existingSubjectMode = !this.canEditPersonalData;

        const personalDisabled = mode === 'LIMITED' || mode === 'CONTACT_ONLY';
        const reasonDescriptionDisabled = mode === 'CONTACT_ONLY';
        const subjectType = subject.subjectType === SubjectType.PERSON ? SubjectType.PERSON : SubjectType.ORGANIZATION;

        this.form.setControl('subjectType', new FormControl({value: subjectType, disabled: true}));
        this.form.addControl('subjectCategory', new FormControl({value: SubjectCategory.POTENTIAL_VICTIM, disabled: true}));
        this.form.addControl('name', new FormControl({value: subject.name, disabled: personalDisabled},
            [Validators.required, Validators.maxLength(MAX_LENGTH)]));
        this.form.addControl('originDate', this.formBuilder.group({
            date: new FormControl({value: subject.origin_date ? new Date(subject.origin_date) : null, disabled: personalDisabled},
                [Validators.required]),
        }));
        this.form.addControl('country', new FormControl({value: subject.country, disabled: personalDisabled},
            [Validators.required]));
        this.form.addControl('phone', new FormControl({value: subject.phone_numbers?.[0]?.number, disabled: personalDisabled},
            [Validators.required]));

        if (subjectType === SubjectType.PERSON) {
            this.form.addControl('surname', new FormControl({value: subject.surname, disabled: personalDisabled},
                [Validators.required, Validators.maxLength(MAX_LENGTH)]));
            this.form.addControl('patronymic', new FormControl({value: subject.patronymic, disabled: personalDisabled},
                [Validators.maxLength(MAX_LENGTH)]));
            this.form.addControl('gender', new FormControl({value: subject.gender, disabled: personalDisabled},
                [Validators.required]));
            this.form.addControl('iin', new FormControl({value: subject.iin, disabled: true},
                [Validators.required, Validators.pattern(IIN_PATTERN)]));
        } else {
            this.form.addControl('type', new FormControl({value: subject.type, disabled: personalDisabled},
                [Validators.required]));
            this.form.addControl('bin', new FormControl({value: subject.bin, disabled: true},
                [Validators.required, Validators.pattern(BIN_PATTERN)]));
        }

        const potentialVictimCategory = activeCategories.find(c => c.category === SubjectCategory.POTENTIAL_VICTIM);
        this.form.addControl('creationReason', new FormControl(
            {value: potentialVictimCategory?.creationReason, disabled: reasonDescriptionDisabled},
            [Validators.required],
        ));
        this.form.addControl('description', new FormControl(
            {value: potentialVictimCategory?.creationDescription, disabled: reasonDescriptionDisabled},
            [Validators.required, Validators.maxLength(2000)],
        ));

        this.cdr.detectChanges();
        this.initialFormValue = cloneDeep(this.form.getRawValue());
        this.subscribeToFormChanges();
    }

    private captureInitialContactDetails(): void {
        this.initialContactDetailsValue = cloneDeep(this.contactDetailsForm.getRawValue());
        this.contactDetailsForm.valueChanges.pipe(
            debounceTime(300),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => {
            this.isContactDetailsChanged = !isEqual(this.initialContactDetailsValue, this.contactDetailsForm.getRawValue());
        });
    }

    private subscribeToFormChanges(): void {
        this.form.valueChanges.pipe(
            debounceTime(300),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => {
            this.isFormChanged = !isEqual(this.initialFormValue, this.form.getRawValue());
        });
    }

    private buildRequisiteData(value: FormGroup<CreateSubjectForm>['value']): Partial<SubjectRequest> {
        const result: Partial<SubjectRequest> = {};
        if (this.form.controls.cardMaskNumber?.valid && value.cardMaskNumber) {
            result.card_numbers = [value.cardMaskNumber];
        }
        if (this.form.controls.ibanKz?.valid && value.ibanKz) {
            result.ibans = [value.ibanKz];
        }
        if (this.form.controls.ibanForeign?.valid && value.ibanForeign) {
            result.foreignIbans = [value.ibanForeign];
        }
        if (this.form.controls.walletNumber?.valid && value.walletNumber) {
            result.walletNumbers = [value.walletNumber];
        }
        if (this.form.controls.paymentPointNumber?.valid && value.paymentPointNumber?.trim()) {
            result.paymentPointNumbers = [value.paymentPointNumber.trim()];
        }
        return result;
    }

    private updateForm(): void {
        this.formSubscriptions$.next();
        const previousCategory = this.form.controls.subjectCategory?.value;
        this.currentStep = 'basicInfo';
        this.existingSubjectMode = false;
        this.removeControllers();
        this.form.addControl('name', new FormControl(null, [Validators.required, Validators.maxLength(MAX_LENGTH)]));
        this.form.addControl('originDate', this.formBuilder.group({
            date: new FormControl(null, [Validators.required]),
        }));
        this.form.addControl('country', new FormControl(null, [Validators.required]));
        if (this.isPotentialVictimOnlyUser) {
            this.form.addControl('subjectCategory', new FormControl(
                {value: SubjectCategory.POTENTIAL_VICTIM, disabled: true},
                [Validators.required],
            ));
        } else {
            this.form.addControl('subjectCategory', new FormControl(null, [Validators.required]));
        }
        this.form.addControl('phone', new FormControl(null, [Validators.required]));
        this.form.addControl('cardMaskNumber', new FormControl(null));
        this.form.addControl('ibanKz', new FormControl(null));
        this.form.addControl('ibanForeign', new FormControl(null));
        this.form.addControl('walletNumber', new FormControl(''));
        this.form.addControl('paymentPointNumber', new FormControl(''));

        if (this.form.value.subjectType === SubjectType.PERSON) {
            this.addPersonControls();
        } else {
            this.addOrganizationControls();
        }

        this.form.addControl('requisiteType', new FormControl(null));
        this.form.addControl('creationReason', new FormControl(null));
        this.form.addControl('description', new FormControl(null, [Validators.maxLength(2000)]));

        this.form.controls.subjectCategory.valueChanges
            .pipe(takeUntil(this.formSubscriptions$), takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                this.isPotentialVictim = value === SubjectCategory.POTENTIAL_VICTIM;
                if (this.isPotentialVictim) {
                    this.form.controls.creationReason?.setValidators([Validators.required]);
                    this.form.controls.description?.setValidators([Validators.required]);
                } else {
                    this.form.controls.creationReason?.clearValidators();
                    this.form.controls.description?.clearValidators();
                    this.form.controls.creationReason?.reset();
                    this.form.controls.description?.reset();
                    this.currentStep = 'basicInfo';
                }
                this.form.controls.creationReason?.updateValueAndValidity();
                this.form.controls.description?.updateValueAndValidity();
            });

        this.form.controls.requisiteType?.valueChanges
            .pipe(takeUntil(this.formSubscriptions$), takeUntilDestroyed(this.destroyRef))
            .subscribe(type => this.updateRequisiteValidators(type));

        if (previousCategory) {
            this.form.controls.subjectCategory.setValue(previousCategory);
        } else {
            this.isPotentialVictim = false;
        }

        this.cdr.detectChanges();
    }

    private updateRequisiteValidators(type: string): void {
        const baseValidatorsMap: Record<string, ValidatorFn[]> = {
            ibanKz: [
                Validators.minLength(KZ_IBAN_LENGTH),
                Validators.maxLength(KZ_IBAN_LENGTH),
                Validators.pattern(KZ_IBAN_PATTERN),
            ],
            ibanForeign: [
                Validators.minLength(FOREIGN_IBAN_MIN_LENGTH),
                Validators.maxLength(IBAN_MAX_LENGTH),
                Validators.pattern(FOREIGN_IBAN_PATTERN),
            ],
            walletNumber: [
                Validators.minLength(WALLET_NUMBER_MIN_LENGTH),
                Validators.maxLength(WALLET_NUMBER_MAX_LENGTH),
                Validators.pattern(NUMBER_PATTERN),
            ],
            paymentPointNumber: [
                Validators.maxLength(MAX_PAYMENT_POINT_NUMBER),
                Validators.pattern(PATTERN_PAYMENT_POINT_NUMBER),
            ],
            cardMaskNumber: [Validators.maxLength(CARD_NUMBER_MAX_LENGTH)],
        };
        const requiredControlByType: Record<string, string> = {
            ACCOUNT_KZ: 'ibanKz',
            ACCOUNT_FOREIGN_BANK: 'ibanForeign',
            ELECTRONIC_WALLET: 'walletNumber',
            PAYMENT_POINT: 'paymentPointNumber',
            CARD_MASK: 'cardMaskNumber',
        };

        for (const [controlName, validators] of Object.entries(baseValidatorsMap)) {
            const control = this.form.get(controlName);
            if (!control) {
                continue;
            }
            const allValidators = controlName === requiredControlByType[type]
                ? [Validators.required, ...validators]
                : validators;
            control.setValidators(allValidators);
            control.updateValueAndValidity();
        }
    }

    private addPersonControls(): void {
        this.form.addControl('surname', new FormControl(null, [Validators.required, Validators.maxLength(MAX_LENGTH)]));
        this.form.addControl('patronymic', new FormControl(null, [Validators.maxLength(MAX_LENGTH)]));
        this.form.addControl('gender', new FormControl(null, [Validators.required]));
        this.form.addControl('iin', new FormControl(null, [Validators.required, Validators.pattern(IIN_PATTERN)]));
    }

    private addOrganizationControls(): void {
        this.form.addControl('type', new FormControl(null, [Validators.required]));
        this.form.addControl('bin', new FormControl(null, [Validators.required, Validators.pattern(BIN_PATTERN)]));
    }

    private removeControllers(): void {
        Object.keys(this.form.controls)
            .filter((controlName): controlName is keyof CreateSubjectForm => controlName !== 'subjectType')
            .forEach(controlName => this.form.removeControl(controlName, {emitEvent: true}));
    }

    private showAlreadyPotentialVictimDialog(subjectId: string): void {
        this.dialogService.openDialog({
            icon: 'warning',
            title: 'i18n_af_dialogSubjectAlreadyPotentialVictimTitle',
            subtitle: 'i18n_af_dialogSubjectAlreadyPotentialVictimSubtitle',
            buttonOkText: 'i18n_af_buttonYesOpen',
            buttonCancelText: 'i18n_af_buttonCancel',
        }).subscribe(result => {
            if (result) {
                this.navigateToSubjectCard(subjectId);
            }
        });
    }

    private showSubjectInOtherListsDialog(existingSubject: SubjectCheckInfo): void {
        this.dialogService.openDialog({
            icon: 'warning',
            title: 'i18n_af_dialogSubjectInOtherListsTitle',
            subtitle: 'i18n_af_dialogSubjectInOtherListsSubtitle',
            buttonOkText: 'i18n_af_buttonYesCreate',
            buttonCancelText: 'i18n_af_buttonNoOpenSubjectCard',
        }).subscribe(result => {
            if (result) {
                this.fillFormFromExistingSubject(existingSubject);
            } else {
                this.navigateToSubjectCard(existingSubject.id);
            }
        });
    }

    private fillFormFromExistingSubject(subject: SubjectCheckInfo): void {
        this.existingSubjectMode = true;
        this.isPotentialVictim = true;

        this.form.setControl('subjectCategory', new FormControl({value: SubjectCategory.POTENTIAL_VICTIM, disabled: true}));
        this.form.setControl('country', new FormControl({value: subject.country, disabled: true}));
        this.form.setControl('phone', new FormControl({value: subject.phoneNumber, disabled: true}));
        this.form.setControl('name', new FormControl({value: subject.name, disabled: true}));
        this.form.setControl('originDate', this.formBuilder.group({
            date: new FormControl({value: subject.originDate ? new Date(subject.originDate) : null, disabled: true}),
        }));
        if (this.form.controls.surname) {
            this.form.setControl('surname', new FormControl({value: subject.surname, disabled: true}));
        }
        if (this.form.controls.patronymic) {
            this.form.setControl('patronymic', new FormControl({value: subject.patronymic, disabled: true}));
        }
        if (this.form.controls.gender) {
            this.form.setControl('gender', new FormControl({value: subject.gender, disabled: true}));
        }
        if (this.form.controls.iin) {
            this.form.setControl('iin', new FormControl({value: subject.iin, disabled: true}));
        }
        if (this.form.controls.type) {
            this.form.setControl('type', new FormControl({value: subject.type, disabled: true}));
        }
        if (this.form.controls.bin) {
            this.form.setControl('bin', new FormControl({value: subject.bin, disabled: true}));
        }
        this.form.controls.subjectType.disable({emitEvent: false});

        this.form.controls.creationReason?.setValidators([Validators.required]);
        this.form.controls.description?.setValidators([Validators.required]);
        this.form.controls.creationReason?.updateValueAndValidity();
        this.form.controls.description?.updateValueAndValidity();
        this.cdr.detectChanges();
    }

    private navigateToSubjectCard(subjectId: string): void {
        this.routerService.navigateToTab(`${Path.SUBJECTS}/${subjectId}`, this.tab);
    }
}

type SubjectEditMode = 'FULL' | 'LIMITED' | 'CONTACT_ONLY' | 'NO_PV';

interface CreateSubjectForm {
    subjectType: FormControl<SubjectType>;
    subjectCategory?: FormControl<SubjectCategory>;
    country?: FormControl<string>;
    phone?: FormControl<string>;
    name?: FormControl<string>;
    originDate?: FormGroup<DateForm>;
    surname?: FormControl<string>;
    patronymic?: FormControl<string>;
    gender?: FormControl<string>;
    iin?: FormControl<string>;
    type?: FormControl<string>;
    bin?: FormControl<string>;
    cardMaskNumber?: FormControl<string>;
    ibanKz?: FormControl<string>;
    ibanForeign?: FormControl<string>;
    requisiteType?: FormControl<string>;
    paymentPointNumber?: FormControl<string>;
    walletNumber?: FormControl<string>;
    creationReason?: FormControl<SubjectCategoryCreationReason>;
    description?: FormControl<string>;
}
